import { ChevronDown } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

interface FAQItem {
  question: string
  answer: string
}

function AccordionItem({ item, isOpen, isDimmed, onToggle }: { item: FAQItem; isOpen: boolean; isDimmed: boolean; onToggle: () => void }) {
  return (
    <div className={`relative transition-all duration-300 ${isOpen ? 'z-30' : 'z-0'} ${isDimmed ? 'opacity-15 blur-[1px] scale-[0.98]' : 'opacity-100 blur-0 scale-100'}`}>
      <button
        onClick={onToggle}
        className={`w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer bg-white rounded-xl border transition-all hover:shadow-md ${isOpen ? 'border-emerald-300 shadow-md' : 'border-slate-200'}`}
        aria-expanded={isOpen}
      >
        <span className="text-base font-semibold text-slate-900 pr-4 font-nunito">{item.question}</span>
        <ChevronDown
          className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      {isOpen && (
        <div className="absolute left-0 right-0 top-full z-30 mt-1 rounded-xl border border-emerald-200 shadow-2xl px-6 py-5 text-slate-600 leading-relaxed font-nunito animate-in fade-in slide-in-from-top-2 duration-200 bg-white">
          {item.answer}
        </div>
      )}
    </div>
  )
}

export function FAQSection() {
  const { t } = useTranslation()
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  const items = t('faq.items', { returnObjects: true }) as FAQItem[]

  return (
    <section id="faq" className="py-20 bg-slate-50">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 xl:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3 font-nunito">
              {t('faq.title')}
            </h2>
            <p className="text-slate-500 text-lg font-nunito max-w-2xl mx-auto">
              {t('faq.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {items.map((item, index) => (
              <AccordionItem
                key={index}
                item={item}
                isOpen={openIndex === index}
                isDimmed={openIndex !== null && openIndex !== index}
                onToggle={() => setOpenIndex(openIndex === index ? null : index)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
